import { and, rankWith, schemaTypeIs, scopeEndsWith, uiTypeIs } from '@jsonforms/core';
import { useEffect, useState } from 'react';
import { Group, NumberInput, Slider, Stack, Text } from '@mantine/core';
import { MantineInputControl } from './MantineInputControl';
import { ControlProps } from '../../types/controlProps';
import { withJsonFormsControlProps } from '../utils/withJsonFormsPropsMappers';
import { getDefaultValue } from '../../hooks/useSelectedObject';
import { useIsParentSelected } from '../../hooks/useIsParentSelected';
import { useDebouncedCallback } from 'use-debounce';

const MantineSliderControlBase = (props: ControlProps) => {
  const { data, errors, schema, handleChange, visible, path } = props;
  const [value, setValue] = useState(data);

  const { isParentSelected } = useIsParentSelected(path);
  const disabled = !isParentSelected;

  const debouncedChange = useDebouncedCallback((value: number) => {
    handleChange(path, value);
  }, 250);

  const onChange = (value: number) => {
    debouncedChange(value);
    setValue(value);
  };

  useEffect(() => {
    setValue(data);
  }, [data, setValue]);

  if (!visible) {
    return null;
  }

  const middle = ((schema.maximum ?? 0) + (schema.minimum ?? 100)) / 2;
  const fallbackValue = disabled ? middle : getDefaultValue(path);

  return (
    <Stack gap='xs' mb='md'>
      <Group wrap='nowrap'>
        <Slider
          label={null}
          disabled={disabled}
          miw={220}
          maw={320}
          min={schema.minimum}
          max={schema.maximum}
          value={value ?? fallbackValue}
          onChange={onChange}
          step={1}
          marks={[
            {
              value: schema.minimum ?? 0,
              label: `${schema.minimum}%`,
            },
            {
              value: schema.maximum ?? 100,
              label: `${schema.maximum}%`,
            },
            {
              value: middle,
              label: `${middle}%`,
            },
          ]}
        />
        <NumberInput
          rightSection={<></>}
          rightSectionWidth={0}
          step={0.01}
          w={60}
          min={schema.minimum}
          max={schema.maximum}
          value={value ?? fallbackValue}
          onBlur={(e) => (e.currentTarget.value = value ?? fallbackValue)}
          onChange={(e) => {
            if (e !== '') {
              onChange(Number(e));
            }
          }}
        />
      </Group>
    </Stack>
  );
};

export const MantineSliderControl = (props: ControlProps) => {
  return <MantineInputControl {...props} input={MantineSliderControlBase} />;
};

export default withJsonFormsControlProps(MantineSliderControl);

export const mantineSliderControlTester = rankWith(
  3,
  and(uiTypeIs('Control'), schemaTypeIs('number'), scopeEndsWith('percentage'))
);
